// Mock data for classroom engagement carousel

export interface MockPlayer {
  id: string;
  name: string;
  score: number;
}

export interface MockQuestion {
  id: string;
  question: string;
  answers: string[];
  correct_index: number;
  explanation: string;
}

export interface MockQuestionStat {
  question_id: string;
  correct_count: number;
  total_answered: number;
  correct_percentage: number;
  average_time_seconds: number;
  answer_distribution: { [key: string]: number };
}

// Generate realistic student names
export const mockPlayers: MockPlayer[] = [
  { id: '1', name: 'Emma Johnson', score: 2850 },
  { id: '2', name: 'Liam Chen', score: 2720 },
  { id: '3', name: 'Sofia Rodriguez', score: 2680 },
  { id: '4', name: 'Noah Anderson', score: 2540 },
  { id: '5', name: 'Olivia Kim', score: 2480 },
  { id: '6', name: 'Ethan Williams', score: 2350 },
  { id: '7', name: 'Ava Martinez', score: 2280 },
  { id: '8', name: 'Mason Taylor', score: 2150 },
  { id: '9', name: 'Isabella Brown', score: 2080 },
  { id: '10', name: 'Lucas Davis', score: 1950 },
  { id: '11', name: 'Mia Wilson', score: 1920 },
  { id: '12', name: 'Aiden Garcia', score: 1850 },
  { id: '13', name: 'Charlotte Lee', score: 1780 },
  { id: '14', name: 'Jackson Miller', score: 1720 },
  { id: '15', name: 'Amelia Thomas', score: 1650 },
  { id: '16', name: 'Elijah Moore', score: 1580 },
  { id: '17', name: 'Harper White', score: 1480 },
  { id: '18', name: 'James Harris', score: 1350 },
];

// Sample quiz about Solar System
export const mockQuiz = {
  title: 'The Solar System',
  description: 'Test your knowledge about our cosmic neighborhood',
  questions: [
    {
      id: 'q1',
      question: 'Which planet is known as the Red Planet?',
      answers: ['Venus', 'Mars', 'Jupiter', 'Saturn'],
      correct_index: 1,
      explanation: 'Mars appears red due to iron oxide (rust) on its surface.'
    },
    {
      id: 'q2',
      question: 'How many planets are in our Solar System?',
      answers: ['7', '8', '9', '10'],
      correct_index: 1,
      explanation: 'There are 8 planets since Pluto was reclassified as a dwarf planet in 2006.'
    },
    {
      id: 'q3',
      question: 'Which planet is the largest in our Solar System?',
      answers: ['Saturn', 'Neptune', 'Jupiter', 'Uranus'],
      correct_index: 2,
      explanation: 'Jupiter is more than twice as massive as all other planets combined.'
    },
    {
      id: 'q4',
      question: 'What is the hottest planet in our Solar System?',
      answers: ['Mercury', 'Venus', 'Mars', 'Earth'],
      correct_index: 1,
      explanation: 'Venus is hotter than Mercury due to its thick atmosphere creating a greenhouse effect.'
    },
    {
      id: 'q5',
      question: 'Which planet has the most moons?',
      answers: ['Jupiter', 'Saturn', 'Uranus', 'Neptune'],
      correct_index: 1,
      explanation: 'Saturn has 146 confirmed moons, surpassing Jupiter\'s 95 moons.'
    },
    {
      id: 'q6',
      question: 'How long does it take Earth to orbit the Sun?',
      answers: ['365 days', '360 days', '366 days', '364 days'],
      correct_index: 0,
      explanation: 'Earth takes approximately 365.25 days to complete one orbit around the Sun.'
    },
    {
      id: 'q7',
      question: 'Which planet is tilted on its side?',
      answers: ['Mars', 'Saturn', 'Uranus', 'Neptune'],
      correct_index: 2,
      explanation: 'Uranus rotates on its side with an axial tilt of about 98 degrees.'
    },
    {
      id: 'q8',
      question: 'What is the smallest planet in our Solar System?',
      answers: ['Mercury', 'Mars', 'Venus', 'Pluto'],
      correct_index: 0,
      explanation: 'Mercury is the smallest planet, only slightly larger than Earth\'s Moon.'
    },
    {
      id: 'q9',
      question: 'Which planet has the Great Red Spot?',
      answers: ['Saturn', 'Jupiter', 'Mars', 'Neptune'],
      correct_index: 1,
      explanation: 'The Great Red Spot is a massive storm on Jupiter that has lasted for centuries.'
    },
    {
      id: 'q10',
      question: 'What are Saturn\'s rings made of?',
      answers: ['Rock and dust', 'Ice and rock', 'Gas', 'Metal'],
      correct_index: 1,
      explanation: 'Saturn\'s rings are made of billions of particles of ice and rock.'
    }
  ] as MockQuestion[]
};

// Question statistics with realistic distributions
export const mockQuestionStats: MockQuestionStat[] = [
  {
    question_id: 'q1',
    correct_count: 16,
    total_answered: 18,
    correct_percentage: 88.9,
    average_time_seconds: 7.2,
    answer_distribution: { '0': 1, '1': 16, '2': 0, '3': 1 }
  },
  {
    question_id: 'q2',
    correct_count: 15,
    total_answered: 18,
    correct_percentage: 83.3,
    average_time_seconds: 8.5,
    answer_distribution: { '0': 2, '1': 15, '2': 1, '3': 0 }
  },
  {
    question_id: 'q3',
    correct_count: 17,
    total_answered: 18,
    correct_percentage: 94.4,
    average_time_seconds: 6.8,
    answer_distribution: { '0': 0, '1': 1, '2': 17, '3': 0 }
  },
  {
    question_id: 'q4',
    correct_count: 9,
    total_answered: 18,
    correct_percentage: 50.0,
    average_time_seconds: 11.3,
    answer_distribution: { '0': 7, '1': 9, '2': 2, '3': 0 }
  },
  {
    question_id: 'q5',
    correct_count: 8,
    total_answered: 18,
    correct_percentage: 44.4,
    average_time_seconds: 13.1,
    answer_distribution: { '0': 6, '1': 8, '2': 3, '3': 1 }
  },
  {
    question_id: 'q6',
    correct_count: 14,
    total_answered: 18,
    correct_percentage: 77.8,
    average_time_seconds: 9.2,
    answer_distribution: { '0': 14, '1': 2, '2': 1, '3': 1 }
  },
  {
    question_id: 'q7',
    correct_count: 10,
    total_answered: 18,
    correct_percentage: 55.6,
    average_time_seconds: 12.4,
    answer_distribution: { '0': 3, '1': 3, '2': 10, '3': 2 }
  },
  {
    question_id: 'q8',
    correct_count: 16,
    total_answered: 18,
    correct_percentage: 88.9,
    average_time_seconds: 7.8,
    answer_distribution: { '0': 16, '1': 2, '2': 0, '3': 0 }
  },
  {
    question_id: 'q9',
    correct_count: 15,
    total_answered: 18,
    correct_percentage: 83.3,
    average_time_seconds: 8.9,
    answer_distribution: { '0': 1, '1': 15, '2': 1, '3': 1 }
  },
  {
    question_id: 'q10',
    correct_count: 13,
    total_answered: 18,
    correct_percentage: 72.2,
    average_time_seconds: 10.1,
    answer_distribution: { '0': 3, '1': 13, '2': 2, '3': 0 }
  }
];

// Aggregate class statistics
export const mockAggregateStats = {
  total_players: 18,
  total_questions: 10,
  class_average_score: 2145,
  class_average_percent: 73.8,
  median_score: 2065,
  std_deviation: 18.3,
  score_distribution: {
    '0-500': 0,
    '501-1000': 0,
    '1001-1500': 3,
    '1501-2000': 6,
    '2001-2500': 6,
    '2501-3000': 3
  }
};

// Network health data
export const mockNetworkHealth = {
  average_network_lag_ms: 142,
  median_lag_ms: 128,
  std_dev_lag_ms: 45,
  sample_count: 180,
  problematic_players: [],
  problematic_questions: []
};

// Identify problem questions (< 60% correct)
export const problemQuestions = mockQuiz.questions.filter((q, idx) => {
  const stat = mockQuestionStats[idx];
  return stat.correct_percentage < 60;
});

// Identify strong questions (>= 80% correct)
export const strongQuestions = mockQuiz.questions.filter((q, idx) => {
  const stat = mockQuestionStats[idx];
  return stat.correct_percentage >= 80;
});
